import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Check, ImagePlus, ScrollText, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errText } from "@/lib/api";
import { logoUrl, setBrandingCache, useBranding } from "@/components/Letterhead";

export const BrandingSettings = () => {
  const branding = useBranding();
  const [form, setForm] = useState({ name: "", line1: "", line2: "", line3: "" });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (branding) {
      setForm({
        name: branding.name || "",
        line1: branding.line1 || "",
        line2: branding.line2 || "",
        line3: branding.line3 || "",
      });
    }
  }, [branding]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/branding", form);
      setBrandingCache(data);
      toast.success("Letterhead saved");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/branding/logo", body);
      setBrandingCache(data);
      toast.success("Logo added to every sheet");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/branding/logo");
      setBrandingCache(data);
      toast.success("Logo removed");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  const logo = logoUrl(branding);

  return (
    <section className="surface p-6 lg:p-8" data-testid="branding-section">
      <h2 className="text-base md:text-lg font-heading font-semibold inline-flex items-center gap-2">
        <ScrollText className="h-5 w-5" /> Letterhead
      </h2>
      <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
        Your logo and chambers lines head every sheet, brief, day kit and travel pack — on screen, in
        print and in every PDF.
      </p>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <div
          data-testid="branding-logo-preview"
          className="h-20 w-40 shrink-0 border border-border rounded-sm bg-secondary grid place-items-center overflow-hidden"
        >
          {logo ? (
            <img src={logo} alt="" className="max-h-16 max-w-[9rem] object-contain" />
          ) : (
            <span className="text-xs text-muted-foreground">No logo</span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg"
            data-testid="branding-logo-input"
            onChange={(e) => upload(e.target.files?.[0])}
            className="hidden"
          />
          <Button
            variant="outline"
            data-testid="branding-logo-choose-btn"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
            className="rounded-full h-11 px-5"
          >
            <ImagePlus className="h-4 w-4 mr-2" /> {logo ? "Change logo" : "Upload logo"}
          </Button>
          {logo ? (
            <Button
              variant="outline"
              data-testid="branding-logo-remove-btn"
              disabled={busy}
              onClick={removeLogo}
              className="rounded-full h-11 px-5 text-destructive border-destructive/40 hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Remove
            </Button>
          ) : null}
        </div>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">PNG or JPEG up to 4 MB.</p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2 sm:col-span-2">
          <Label className="label-caps">Chambers name</Label>
          <Input
            data-testid="branding-name-input"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Chambers of Amir Arsiwala"
            className="rounded-sm h-11"
          />
        </div>
        {[1, 2, 3].map((n) => (
          <div key={n} className="space-y-2">
            <Label className="label-caps">Line {n}</Label>
            <Input
              data-testid={`branding-line${n}-input`}
              value={form[`line${n}`]}
              onChange={(e) => setForm({ ...form, [`line${n}`]: e.target.value })}
              placeholder={
                n === 1 ? "Chamber address" : n === 2 ? "Phone" : "Email or website"
              }
              className="rounded-sm h-11"
            />
          </div>
        ))}
      </div>

      <Button
        data-testid="branding-save-btn"
        onClick={save}
        disabled={busy}
        className="mt-6 rounded-full h-11 px-6"
      >
        <Check className="h-4 w-4 mr-2" /> {busy ? "Saving…" : "Save letterhead"}
      </Button>
    </section>
  );
};
